import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  MapPin, Star, Clock, Users, ChevronRight, ChevronLeft, Sparkles,
  Search, Plane, Filter, Grid, List, Calendar, X, SlidersHorizontal,
  Home as HomeIcon, Building, Map
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country center coordinates
const COUNTRY_CENTERS = {
  // América del Norte
  'México': { lat: 23.6345, lng: -102.5528, zoom: 5 },
  'Estados Unidos': { lat: 37.0902, lng: -95.7129, zoom: 4 },
  'Canadá': { lat: 56.1304, lng: -106.3468, zoom: 4 },
  // Caribe
  'República Dominicana': { lat: 18.7357, lng: -70.1627, zoom: 8 },
  'Cuba': { lat: 21.5218, lng: -77.7812, zoom: 6 },
  'Puerto Rico': { lat: 18.2208, lng: -66.5901, zoom: 9 },
  'Jamaica': { lat: 18.1096, lng: -77.2975, zoom: 8 },
  'Bahamas': { lat: 25.0343, lng: -77.3963, zoom: 7 },
  'Aruba': { lat: 12.5211, lng: -69.9683, zoom: 11 },
  'Curazao': { lat: 12.1696, lng: -68.9900, zoom: 11 },
  'Barbados': { lat: 13.1939, lng: -59.5432, zoom: 10 },
  'Trinidad y Tobago': { lat: 10.6918, lng: -61.2225, zoom: 9 },
  'San Martín': { lat: 18.0425, lng: -63.0548, zoom: 11 },
  'Islas Caimán': { lat: 19.3133, lng: -81.2546, zoom: 9 },
  'Islas Vírgenes': { lat: 18.3358, lng: -64.8963, zoom: 9 },
  'Turks y Caicos': { lat: 21.6940, lng: -71.7979, zoom: 9 },
  'Antigua y Barbuda': { lat: 17.0608, lng: -61.7964, zoom: 10 },
  'Santa Lucía': { lat: 13.9094, lng: -60.9789, zoom: 10 },
  'Granada': { lat: 12.1165, lng: -61.6790, zoom: 10 },
  'San Vicente': { lat: 12.9843, lng: -61.2872, zoom: 10 },
  // América Central
  'Costa Rica': { lat: 9.7489, lng: -83.7534, zoom: 7 },
  'Panamá': { lat: 8.538, lng: -80.7821, zoom: 7 },
  'Guatemala': { lat: 15.7835, lng: -90.2308, zoom: 7 },
  'Belice': { lat: 17.1899, lng: -88.4976, zoom: 8 },
  'Honduras': { lat: 15.2, lng: -86.2419, zoom: 7 },
  'Nicaragua': { lat: 12.8654, lng: -85.2072, zoom: 7 },
  'El Salvador': { lat: 13.7942, lng: -88.8965, zoom: 8 },
  // América del Sur
  'Colombia': { lat: 4.5709, lng: -74.2973, zoom: 5 },
  'Perú': { lat: -9.19, lng: -75.0152, zoom: 5 },
  'Brasil': { lat: -14.235, lng: -51.9253, zoom: 4 },
  'Argentina': { lat: -38.4161, lng: -63.6167, zoom: 4 },
  'Chile': { lat: -35.6751, lng: -71.543, zoom: 4 },
  'Ecuador': { lat: -1.8312, lng: -78.1834, zoom: 6 },
  'Uruguay': { lat: -32.5228, lng: -55.7658, zoom: 6 },
  'Venezuela': { lat: 6.4238, lng: -66.5897, zoom: 5 },
  'Bolivia': { lat: -16.2902, lng: -63.5887, zoom: 5 },
  'Paraguay': { lat: -23.4425, lng: -58.4438, zoom: 6 },
  // Europa Occidental
  'España': { lat: 40.4637, lng: -3.7492, zoom: 5 },
  'Portugal': { lat: 39.3999, lng: -8.2245, zoom: 6 },
  'Francia': { lat: 46.2276, lng: 2.2137, zoom: 5 },
  'Italia': { lat: 41.8719, lng: 12.5674, zoom: 5 },
  'Reino Unido': { lat: 55.3781, lng: -3.4360, zoom: 5 },
  'Irlanda': { lat: 53.1424, lng: -7.6921, zoom: 6 },
  'Alemania': { lat: 51.1657, lng: 10.4515, zoom: 5 },
  'Países Bajos': { lat: 52.1326, lng: 5.2913, zoom: 7 },
  'Bélgica': { lat: 50.5039, lng: 4.4699, zoom: 7 },
  'Suiza': { lat: 46.8182, lng: 8.2275, zoom: 7 },
  'Austria': { lat: 47.5162, lng: 14.5501, zoom: 6 },
  'Mónaco': { lat: 43.7384, lng: 7.4246, zoom: 13 },
  // Mediterráneo
  'Grecia': { lat: 39.0742, lng: 21.8243, zoom: 6 },
  'Croacia': { lat: 45.1, lng: 15.2, zoom: 6 },
  'Montenegro': { lat: 42.7087, lng: 19.3744, zoom: 8 },
  'Albania': { lat: 41.1533, lng: 20.1683, zoom: 7 },
  'Malta': { lat: 35.9375, lng: 14.3754, zoom: 10 },
  'Chipre': { lat: 35.1264, lng: 33.4299, zoom: 8 },
  'Turquía': { lat: 38.9637, lng: 35.2433, zoom: 5 },
  // Europa del Norte
  'Noruega': { lat: 60.4720, lng: 8.4689, zoom: 4 },
  'Suecia': { lat: 60.1282, lng: 18.6435, zoom: 4 },
  'Dinamarca': { lat: 56.2639, lng: 9.5018, zoom: 6 },
  'Finlandia': { lat: 61.9241, lng: 25.7482, zoom: 4 },
  'Islandia': { lat: 64.9631, lng: -19.0208, zoom: 5 },
  // Europa del Este
  'Polonia': { lat: 51.9194, lng: 19.1451, zoom: 5 },
  'República Checa': { lat: 49.8175, lng: 15.4730, zoom: 6 },
  'Hungría': { lat: 47.1625, lng: 19.5033, zoom: 6 },
  'Rumania': { lat: 45.9432, lng: 24.9668, zoom: 5 },
  'Bulgaria': { lat: 42.7339, lng: 25.4858, zoom: 6 },
  // Asia del Sudeste
  'Tailandia': { lat: 15.8700, lng: 100.9925, zoom: 5 },
  'Indonesia': { lat: -0.7893, lng: 113.9213, zoom: 4 },
  'Vietnam': { lat: 14.0583, lng: 108.2772, zoom: 5 },
  'Filipinas': { lat: 12.8797, lng: 121.7740, zoom: 5 },
  'Malasia': { lat: 4.2105, lng: 101.9758, zoom: 5 },
  'Singapur': { lat: 1.3521, lng: 103.8198, zoom: 10 },
  'Camboya': { lat: 12.5657, lng: 104.9910, zoom: 6 },
  'Myanmar': { lat: 21.9162, lng: 95.9560, zoom: 5 },
  'Laos': { lat: 19.8563, lng: 102.4955, zoom: 6 },
  // Asia del Este
  'Japón': { lat: 36.2048, lng: 138.2529, zoom: 5 },
  'Corea del Sur': { lat: 35.9078, lng: 127.7669, zoom: 6 },
  'China': { lat: 35.8617, lng: 104.1954, zoom: 4 },
  'Hong Kong': { lat: 22.3193, lng: 114.1694, zoom: 10 },
  'Taiwán': { lat: 23.6978, lng: 120.9605, zoom: 7 },
  // Asia del Sur
  'India': { lat: 20.5937, lng: 78.9629, zoom: 4 },
  'Sri Lanka': { lat: 7.8731, lng: 80.7718, zoom: 7 },
  'Nepal': { lat: 28.3949, lng: 84.1240, zoom: 6 },
  'Maldivas': { lat: 3.2028, lng: 73.2207, zoom: 6 },
  'Bután': { lat: 27.5142, lng: 90.4336, zoom: 7 },
  // Medio Oriente
  'Emiratos Árabes Unidos': { lat: 23.4241, lng: 53.8478, zoom: 6 },
  'Qatar': { lat: 25.3548, lng: 51.1839, zoom: 8 },
  'Arabia Saudita': { lat: 23.8859, lng: 45.0792, zoom: 5 },
  'Omán': { lat: 21.4735, lng: 55.9754, zoom: 6 },
  'Jordania': { lat: 30.5852, lng: 36.2384, zoom: 7 },
  'Israel': { lat: 31.0461, lng: 34.8516, zoom: 7 },
  'Líbano': { lat: 33.8547, lng: 35.8623, zoom: 8 },
  'Baréin': { lat: 26.0667, lng: 50.5577, zoom: 9 },
  // África
  'Marruecos': { lat: 31.7917, lng: -7.0926, zoom: 5 },
  'Egipto': { lat: 26.8206, lng: 30.8025, zoom: 5 },
  'Sudáfrica': { lat: -30.5595, lng: 22.9375, zoom: 5 },
  'Kenia': { lat: -0.0236, lng: 37.9062, zoom: 5 },
  'Tanzania': { lat: -6.3690, lng: 34.8888, zoom: 5 },
  'Mauricio': { lat: -20.3484, lng: 57.5522, zoom: 9 },
  'Seychelles': { lat: -4.6796, lng: 55.4920, zoom: 9 },
  'Madagascar': { lat: -18.7669, lng: 46.8691, zoom: 5 },
  'Túnez': { lat: 33.8869, lng: 9.5375, zoom: 6 },
  'Cabo Verde': { lat: 16.5388, lng: -23.0418, zoom: 8 },
  'Zanzíbar': { lat: -6.1659, lng: 39.2026, zoom: 9 },
  'Namibia': { lat: -22.9576, lng: 18.4904, zoom: 5 },
  'Botsuana': { lat: -22.3285, lng: 24.6849, zoom: 5 },
  'Ruanda': { lat: -1.9403, lng: 29.8739, zoom: 8 },
  'Uganda': { lat: 1.3733, lng: 32.2903, zoom: 6 },
  'Etiopía': { lat: 9.1450, lng: 40.4897, zoom: 5 },
  'Ghana': { lat: 7.9465, lng: -1.0232, zoom: 6 },
  'Nigeria': { lat: 9.0820, lng: 8.6753, zoom: 5 },
  'Senegal': { lat: 14.4974, lng: -14.4524, zoom: 6 },
  'Costa de Marfil': { lat: 7.5400, lng: -5.5471, zoom: 6 },
  'Mozambique': { lat: -18.6657, lng: 35.5296, zoom: 5 },
  'Zimbabue': { lat: -19.0154, lng: 29.1549, zoom: 6 },
  'Zambia': { lat: -13.1339, lng: 27.8493, zoom: 5 },
  'Malaui': { lat: -13.2543, lng: 34.3015, zoom: 6 },
  'Gambia': { lat: 13.4432, lng: -15.3101, zoom: 8 },
  'Reunión': { lat: -21.1151, lng: 55.5364, zoom: 9 },
  'Argelia': { lat: 28.0339, lng: 1.6596, zoom: 5 },
  'Libia': { lat: 26.3351, lng: 17.2283, zoom: 5 },
  // Oceanía
  'Australia': { lat: -25.2744, lng: 133.7751, zoom: 4 },
  'Nueva Zelanda': { lat: -40.9006, lng: 174.8860, zoom: 5 },
  'Fiyi': { lat: -17.7134, lng: 178.0650, zoom: 7 },
  'Polinesia Francesa': { lat: -17.6797, lng: -149.4068, zoom: 6 },
  'Hawái': { lat: 19.8968, lng: -155.5828, zoom: 6 },
  'Samoa': { lat: -13.7590, lng: -172.1046, zoom: 8 },
  'Tonga': { lat: -21.1790, lng: -175.1982, zoom: 8 },
  'Vanuatu': { lat: -15.3767, lng: 166.9592, zoom: 6 },
  'Nueva Caledonia': { lat: -20.9043, lng: 165.6180, zoom: 7 },
  'Islas Cook': { lat: -21.2367, lng: -159.7777, zoom: 8 },
  'Bora Bora': { lat: -16.5004, lng: -151.7415, zoom: 11 },
  'Tahití': { lat: -17.6509, lng: -149.4260, zoom: 9 },
  'Palau': { lat: 7.5150, lng: 134.5825, zoom: 9 },
  'Guam': { lat: 13.4443, lng: 144.7937, zoom: 10 },
  'Islas Salomón': { lat: -9.6457, lng: 160.1562, zoom: 6 },
  'Papua Nueva Guinea': { lat: -6.3150, lng: 143.9555, zoom: 5 },
  'Micronesia': { lat: 7.4256, lng: 150.5508, zoom: 6 },
  'Islas Marshall': { lat: 7.1315, lng: 171.1845, zoom: 7 },
  // Más países adicionales
  'Martinica': { lat: 14.6415, lng: -61.0242, zoom: 10 },
  'Guadalupe': { lat: 16.2650, lng: -61.5510, zoom: 9 },
  'Bermudas': { lat: 32.3078, lng: -64.7505, zoom: 11 },
  'Guyana': { lat: 4.8604, lng: -58.9302, zoom: 6 },
  'Surinam': { lat: 3.9193, lng: -56.0278, zoom: 7 },
  'Luxemburgo': { lat: 49.8153, lng: 6.1296, zoom: 10 },
  'Andorra': { lat: 42.5063, lng: 1.5218, zoom: 11 },
  'Eslovenia': { lat: 46.1512, lng: 14.9955, zoom: 8 },
  'Serbia': { lat: 44.0165, lng: 21.0059, zoom: 6 },
  'Bosnia': { lat: 43.9159, lng: 17.6791, zoom: 7 },
  'Macedonia': { lat: 41.5124, lng: 21.7453, zoom: 8 },
  'Kosovo': { lat: 42.6026, lng: 20.9030, zoom: 9 },
  'Estonia': { lat: 58.5953, lng: 25.0136, zoom: 6 },
  'Letonia': { lat: 56.8796, lng: 24.6032, zoom: 6 },
  'Lituania': { lat: 55.1694, lng: 23.8813, zoom: 6 },
  'Eslovaquia': { lat: 48.6690, lng: 19.6990, zoom: 7 },
  'Ucrania': { lat: 48.3794, lng: 31.1656, zoom: 5 },
  'Moldavia': { lat: 47.4116, lng: 28.3699, zoom: 7 },
  'Bielorrusia': { lat: 53.7098, lng: 27.9534, zoom: 6 },
  'Rusia': { lat: 61.5240, lng: 105.3188, zoom: 3 },
  'Brunéi': { lat: 4.5353, lng: 114.7277, zoom: 9 },
  'Timor Oriental': { lat: -8.8742, lng: 125.7275, zoom: 8 },
  'Macao': { lat: 22.1987, lng: 113.5439, zoom: 12 },
  'Mongolia': { lat: 46.8625, lng: 103.8467, zoom: 4 },
  'Pakistán': { lat: 30.3753, lng: 69.3451, zoom: 5 },
  'Bangladesh': { lat: 23.6850, lng: 90.3563, zoom: 6 },
  'Kazajistán': { lat: 48.0196, lng: 66.9237, zoom: 4 },
  'Uzbekistán': { lat: 41.3775, lng: 64.5853, zoom: 5 },
  'Turkmenistán': { lat: 38.9697, lng: 59.5563, zoom: 5 },
  'Kirguistán': { lat: 41.2044, lng: 74.7661, zoom: 6 },
  'Tayikistán': { lat: 38.8610, lng: 71.2761, zoom: 6 },
  'Georgia': { lat: 42.3154, lng: 43.3569, zoom: 7 },
  'Armenia': { lat: 40.0691, lng: 45.0382, zoom: 7 },
  'Azerbaiyán': { lat: 40.1431, lng: 47.5769, zoom: 6 },
  'Kuwait': { lat: 29.3117, lng: 47.4818, zoom: 8 },
  'Irak': { lat: 33.2232, lng: 43.6793, zoom: 5 },
  'Irán': { lat: 32.4279, lng: 53.6880, zoom: 5 },
  'Palestina': { lat: 31.9522, lng: 35.2332, zoom: 8 },
  'default': { lat: 20, lng: 0, zoom: 2 }
};

// Custom price marker icon
const createPriceIcon = (price) => {
  return L.divIcon({
    className: 'custom-price-marker',
    html: `<div style="
      background: linear-gradient(135deg, #14b8a6, #10b981);
      color: white;
      padding: 6px 10px;
      border-radius: 20px;
      font-weight: bold;
      font-size: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      white-space: nowrap;
      border: 2px solid white;
      cursor: pointer;
      pointer-events: auto;
    ">$${price.toLocaleString()}</div>`,
    iconSize: [80, 30],
    iconAnchor: [40, 15]
  });
};

// Map bounds updater component
function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], zoom, { animate: true });
    }
  }, [center, zoom, map]);
  return null;
}

// Package Card Component
function PackageCard({ pkg, onViewDetails }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = pkg.images?.length > 0 ? pkg.images : [
    'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80'
  ];
  
  const bookingPrice = pkg.comparison_booking || pkg.retail_price * 1.8;
  const expediaPrice = pkg.comparison_expedia || 0;
  const despegarPrice = pkg.comparison_despegar || 0;
  const savings = bookingPrice - pkg.retail_price;
  const percentage = Math.round((savings / bookingPrice) * 100);
  const rating = pkg.rating || 4.8;
  const reviews = pkg.reviews_count || 50;

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);
  };

  const getDateLabel = () => {
    switch(pkg.date_type) {
      case 'restricted': return { text: 'Fechas Restringidas', color: 'bg-amber-500', emoji: '🟡' };
      case 'exact': return { text: 'Fechas Exactas', color: 'bg-rose-500', emoji: '🔴' };
      default: return { text: 'Fechas Abiertas', color: 'bg-emerald-500', emoji: '🟢' };
    }
  };
  
  const dateInfo = getDateLabel();

  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-200 flex flex-col h-full"
      data-testid={`package-card-${pkg.certificate_id}`}
    >
      {/* Image Gallery */}
      <div className="relative h-44 overflow-hidden flex-shrink-0">
        <img
          src={images[currentImageIndex]}
          alt={pkg.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        
        {images.length > 1 && (
          <>
            <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft className="w-4 h-4 text-slate-700" />
            </button>
            <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight className="w-4 h-4 text-slate-700" />
            </button>
          </>
        )}
        
        <div className="absolute top-2 left-2 bg-rose-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
          <Sparkles className="w-3 h-3" />
          -{percentage}%
        </div>

        <div className={`absolute top-2 right-2 ${dateInfo.color} text-white px-2 py-0.5 rounded-full text-[10px] font-medium shadow-lg`}>
          {dateInfo.emoji}
        </div>

        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-xs">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{pkg.days}d/{pkg.nights}n</span>
          <span className="flex items-center gap-1 bg-black/30 px-1.5 py-0.5 rounded-full"><Users className="w-3 h-3" />{pkg.max_persons || 2}</span>
        </div>
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <div className="flex items-center gap-1 text-teal-600 text-xs font-medium mb-1">
          <MapPin className="w-3 h-3" />
          <span className="truncate">{pkg.destination}</span>
        </div>

        {/* Fixed height title area */}
        <h3 className="font-bold text-slate-900 text-sm mb-1.5 line-clamp-2 group-hover:text-teal-600 transition-colors leading-tight h-10">
          {pkg.title}
        </h3>

        <div className="flex items-center gap-1.5 mb-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-3 h-3 ${i < Math.floor(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
            ))}
          </div>
          <span className="text-xs text-slate-600">{rating}</span>
        </div>

        {/* TripAdvisor Rating - Fixed height container */}
        <div className="h-6 mb-2">
          {pkg.tripadvisor_rating ? (
            <a 
              href={pkg.tripadvisor_link || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              onClick={(e) => !pkg.tripadvisor_link && e.preventDefault()}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="#00aa6c">
                <circle cx="12" cy="12" r="12"/>
                <circle cx="8" cy="12" r="3" fill="white"/>
                <circle cx="16" cy="12" r="3" fill="white"/>
                <circle cx="8" cy="12" r="1.5" fill="#00aa6c"/>
                <circle cx="16" cy="12" r="1.5" fill="#00aa6c"/>
              </svg>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      pkg.tripadvisor_rating >= i 
                        ? 'bg-[#00aa6c]' 
                        : pkg.tripadvisor_rating >= i - 0.5
                          ? 'bg-gradient-to-r from-[#00aa6c] from-50% to-gray-200 to-50%'
                          : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-[10px] text-slate-500">{pkg.tripadvisor_rating}</span>
            </a>
          ) : null}
        </div>

        {/* Price Comparison - This grows to fill space */}
        <div className="bg-slate-50 rounded-lg p-2 mb-2 space-y-1 flex-grow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-[#003580] rounded flex items-center justify-center">
                <span className="text-white text-[8px] font-bold">B</span>
              </div>
              <span className="text-[10px] text-slate-500">Booking</span>
            </div>
            <span className="text-xs text-slate-400 line-through">${bookingPrice.toLocaleString()}</span>
          </div>
          
          {expediaPrice > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-[#FFCC00] rounded flex items-center justify-center">
                  <span className="text-[#1E3A5F] text-[8px] font-bold">E</span>
                </div>
                <span className="text-[10px] text-slate-500">Expedia</span>
              </div>
              <span className="text-xs text-slate-400 line-through">${expediaPrice.toLocaleString()}</span>
            </div>
          )}
          
          {despegarPrice > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-[#7B2D8E] rounded flex items-center justify-center">
                  <span className="text-white text-[8px] font-bold">D</span>
                </div>
                <span className="text-[10px] text-slate-500">Despegar</span>
              </div>
              <span className="text-xs text-slate-400 line-through">${despegarPrice.toLocaleString()}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-1 border-t border-slate-200">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-gradient-to-r from-teal-500 to-emerald-500 rounded flex items-center justify-center">
                <Plane className="w-2.5 h-2.5 text-white transform -rotate-45" />
              </div>
              <span className="text-[10px] font-semibold text-teal-600">Bombazoo</span>
            </div>
            <span className="text-lg font-bold text-teal-600">${pkg.retail_price.toLocaleString()}</span>
          </div>
          
          <div className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-[10px] font-semibold text-center">
            ¡Ahorras ${savings.toLocaleString()}!
          </div>
        </div>

        {/* Button always at bottom */}
        <Button
          onClick={onViewDetails}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-2 rounded-lg text-sm mt-auto flex-shrink-0"
        >
          Ver Detalles
        </Button>
      </div>
    </div>
  );
}

export default function SearchResortsPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState('all');
  const [dateType, setDateType] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'

  // Lista completa de todos los países organizados por región
  const ALL_COUNTRIES = useMemo(() => [
    // América del Norte
    'México', 'Estados Unidos', 'Canadá',
    // Caribe
    'República Dominicana', 'Cuba', 'Puerto Rico', 'Jamaica', 'Bahamas', 'Aruba', 'Curazao', 
    'Barbados', 'Trinidad y Tobago', 'San Martín', 'Islas Caimán', 'Islas Vírgenes', 
    'Turks y Caicos', 'Antigua y Barbuda', 'Santa Lucía', 'Granada', 'San Vicente',
    // América Central
    'Costa Rica', 'Panamá', 'Guatemala', 'Belice', 'Honduras', 'Nicaragua', 'El Salvador',
    // América del Sur
    'Colombia', 'Perú', 'Brasil', 'Argentina', 'Chile', 'Ecuador', 'Uruguay', 'Venezuela', 'Bolivia', 'Paraguay',
    // Europa
    'España', 'Portugal', 'Francia', 'Italia', 'Reino Unido', 'Irlanda', 'Alemania', 'Países Bajos', 
    'Bélgica', 'Suiza', 'Austria', 'Mónaco', 'Grecia', 'Croacia', 'Montenegro', 'Albania', 'Malta', 
    'Chipre', 'Turquía', 'Noruega', 'Suecia', 'Dinamarca', 'Finlandia', 'Islandia', 'Polonia', 
    'República Checa', 'Hungría', 'Rumania', 'Bulgaria',
    // Asia
    'Tailandia', 'Indonesia', 'Vietnam', 'Filipinas', 'Malasia', 'Singapur', 'Camboya', 'Myanmar', 'Laos',
    'Japón', 'Corea del Sur', 'China', 'Hong Kong', 'Taiwán',
    'India', 'Sri Lanka', 'Nepal', 'Maldivas', 'Bután',
    // Medio Oriente
    'Emiratos Árabes Unidos', 'Qatar', 'Arabia Saudita', 'Omán', 'Jordania', 'Israel', 'Líbano', 'Baréin',
    // África
    'Marruecos', 'Egipto', 'Sudáfrica', 'Kenia', 'Tanzania', 'Mauricio', 'Seychelles', 
    'Madagascar', 'Túnez', 'Cabo Verde', 'Zanzíbar', 'Namibia', 'Botsuana', 'Ruanda',
    // Oceanía
    'Australia', 'Nueva Zelanda', 'Fiyi', 'Polinesia Francesa', 'Hawái', 'Samoa', 'Tonga', 
    'Vanuatu', 'Nueva Caledonia', 'Islas Cook', 'Bora Bora', 'Tahití'
  ], []);

  // Países populares para la barra rápida - TODOS los países organizados por región
  const COUNTRY_BAR = [
    // América del Norte
    { name: 'México', flag: '🇲🇽' },
    { name: 'Estados Unidos', flag: '🇺🇸' },
    { name: 'Canadá', flag: '🇨🇦' },
    // Caribe
    { name: 'República Dominicana', flag: '🇩🇴' },
    { name: 'Cuba', flag: '🇨🇺' },
    { name: 'Puerto Rico', flag: '🇵🇷' },
    { name: 'Jamaica', flag: '🇯🇲' },
    { name: 'Bahamas', flag: '🇧🇸' },
    { name: 'Aruba', flag: '🇦🇼' },
    { name: 'Curazao', flag: '🇨🇼' },
    { name: 'Barbados', flag: '🇧🇧' },
    { name: 'Trinidad y Tobago', flag: '🇹🇹' },
    { name: 'San Martín', flag: '🇸🇽' },
    { name: 'Islas Caimán', flag: '🇰🇾' },
    { name: 'Islas Vírgenes', flag: '🇻🇬' },
    { name: 'Turks y Caicos', flag: '🇹🇨' },
    { name: 'Antigua y Barbuda', flag: '🇦🇬' },
    { name: 'Santa Lucía', flag: '🇱🇨' },
    { name: 'Granada', flag: '🇬🇩' },
    { name: 'San Vicente', flag: '🇻🇨' },
    { name: 'Martinica', flag: '🇲🇶' },
    { name: 'Guadalupe', flag: '🇬🇵' },
    { name: 'Bermudas', flag: '🇧🇲' },
    // América Central
    { name: 'Costa Rica', flag: '🇨🇷' },
    { name: 'Panamá', flag: '🇵🇦' },
    { name: 'Guatemala', flag: '🇬🇹' },
    { name: 'Belice', flag: '🇧🇿' },
    { name: 'Honduras', flag: '🇭🇳' },
    { name: 'Nicaragua', flag: '🇳🇮' },
    { name: 'El Salvador', flag: '🇸🇻' },
    // América del Sur
    { name: 'Colombia', flag: '🇨🇴' },
    { name: 'Perú', flag: '🇵🇪' },
    { name: 'Brasil', flag: '🇧🇷' },
    { name: 'Argentina', flag: '🇦🇷' },
    { name: 'Chile', flag: '🇨🇱' },
    { name: 'Ecuador', flag: '🇪🇨' },
    { name: 'Uruguay', flag: '🇺🇾' },
    { name: 'Venezuela', flag: '🇻🇪' },
    { name: 'Bolivia', flag: '🇧🇴' },
    { name: 'Paraguay', flag: '🇵🇾' },
    { name: 'Guyana', flag: '🇬🇾' },
    { name: 'Surinam', flag: '🇸🇷' },
    // Europa Occidental
    { name: 'España', flag: '🇪🇸' },
    { name: 'Portugal', flag: '🇵🇹' },
    { name: 'Francia', flag: '🇫🇷' },
    { name: 'Italia', flag: '🇮🇹' },
    { name: 'Reino Unido', flag: '🇬🇧' },
    { name: 'Irlanda', flag: '🇮🇪' },
    { name: 'Alemania', flag: '🇩🇪' },
    { name: 'Países Bajos', flag: '🇳🇱' },
    { name: 'Bélgica', flag: '🇧🇪' },
    { name: 'Suiza', flag: '🇨🇭' },
    { name: 'Austria', flag: '🇦🇹' },
    { name: 'Luxemburgo', flag: '🇱🇺' },
    { name: 'Mónaco', flag: '🇲🇨' },
    { name: 'Andorra', flag: '🇦🇩' },
    // Europa del Sur y Mediterráneo
    { name: 'Grecia', flag: '🇬🇷' },
    { name: 'Croacia', flag: '🇭🇷' },
    { name: 'Montenegro', flag: '🇲🇪' },
    { name: 'Albania', flag: '🇦🇱' },
    { name: 'Eslovenia', flag: '🇸🇮' },
    { name: 'Malta', flag: '🇲🇹' },
    { name: 'Chipre', flag: '🇨🇾' },
    { name: 'Turquía', flag: '🇹🇷' },
    { name: 'Serbia', flag: '🇷🇸' },
    { name: 'Bosnia', flag: '🇧🇦' },
    { name: 'Macedonia', flag: '🇲🇰' },
    { name: 'Kosovo', flag: '🇽🇰' },
    // Europa del Norte
    { name: 'Noruega', flag: '🇳🇴' },
    { name: 'Suecia', flag: '🇸🇪' },
    { name: 'Dinamarca', flag: '🇩🇰' },
    { name: 'Finlandia', flag: '🇫🇮' },
    { name: 'Islandia', flag: '🇮🇸' },
    { name: 'Estonia', flag: '🇪🇪' },
    { name: 'Letonia', flag: '🇱🇻' },
    { name: 'Lituania', flag: '🇱🇹' },
    // Europa del Este
    { name: 'Polonia', flag: '🇵🇱' },
    { name: 'República Checa', flag: '🇨🇿' },
    { name: 'Hungría', flag: '🇭🇺' },
    { name: 'Rumania', flag: '🇷🇴' },
    { name: 'Bulgaria', flag: '🇧🇬' },
    { name: 'Eslovaquia', flag: '🇸🇰' },
    { name: 'Ucrania', flag: '🇺🇦' },
    { name: 'Moldavia', flag: '🇲🇩' },
    { name: 'Bielorrusia', flag: '🇧🇾' },
    { name: 'Rusia', flag: '🇷🇺' },
    // Asia del Sudeste
    { name: 'Tailandia', flag: '🇹🇭' },
    { name: 'Indonesia', flag: '🇮🇩' },
    { name: 'Vietnam', flag: '🇻🇳' },
    { name: 'Filipinas', flag: '🇵🇭' },
    { name: 'Malasia', flag: '🇲🇾' },
    { name: 'Singapur', flag: '🇸🇬' },
    { name: 'Camboya', flag: '🇰🇭' },
    { name: 'Myanmar', flag: '🇲🇲' },
    { name: 'Laos', flag: '🇱🇦' },
    { name: 'Brunéi', flag: '🇧🇳' },
    { name: 'Timor Oriental', flag: '🇹🇱' },
    // Asia del Este
    { name: 'Japón', flag: '🇯🇵' },
    { name: 'Corea del Sur', flag: '🇰🇷' },
    { name: 'China', flag: '🇨🇳' },
    { name: 'Hong Kong', flag: '🇭🇰' },
    { name: 'Taiwán', flag: '🇹🇼' },
    { name: 'Macao', flag: '🇲🇴' },
    { name: 'Mongolia', flag: '🇲🇳' },
    // Asia del Sur
    { name: 'India', flag: '🇮🇳' },
    { name: 'Sri Lanka', flag: '🇱🇰' },
    { name: 'Nepal', flag: '🇳🇵' },
    { name: 'Maldivas', flag: '🇲🇻' },
    { name: 'Bután', flag: '🇧🇹' },
    { name: 'Pakistán', flag: '🇵🇰' },
    { name: 'Bangladesh', flag: '🇧🇩' },
    // Asia Central
    { name: 'Kazajistán', flag: '🇰🇿' },
    { name: 'Uzbekistán', flag: '🇺🇿' },
    { name: 'Turkmenistán', flag: '🇹🇲' },
    { name: 'Kirguistán', flag: '🇰🇬' },
    { name: 'Tayikistán', flag: '🇹🇯' },
    { name: 'Georgia', flag: '🇬🇪' },
    { name: 'Armenia', flag: '🇦🇲' },
    { name: 'Azerbaiyán', flag: '🇦🇿' },
    // Medio Oriente
    { name: 'Emiratos Árabes Unidos', flag: '🇦🇪' },
    { name: 'Qatar', flag: '🇶🇦' },
    { name: 'Arabia Saudita', flag: '🇸🇦' },
    { name: 'Omán', flag: '🇴🇲' },
    { name: 'Jordania', flag: '🇯🇴' },
    { name: 'Israel', flag: '🇮🇱' },
    { name: 'Líbano', flag: '🇱🇧' },
    { name: 'Baréin', flag: '🇧🇭' },
    { name: 'Kuwait', flag: '🇰🇼' },
    { name: 'Irak', flag: '🇮🇶' },
    { name: 'Irán', flag: '🇮🇷' },
    { name: 'Palestina', flag: '🇵🇸' },
    // África del Norte
    { name: 'Marruecos', flag: '🇲🇦' },
    { name: 'Egipto', flag: '🇪🇬' },
    { name: 'Túnez', flag: '🇹🇳' },
    { name: 'Argelia', flag: '🇩🇿' },
    { name: 'Libia', flag: '🇱🇾' },
    // África Subsahariana
    { name: 'Sudáfrica', flag: '🇿🇦' },
    { name: 'Kenia', flag: '🇰🇪' },
    { name: 'Tanzania', flag: '🇹🇿' },
    { name: 'Mauricio', flag: '🇲🇺' },
    { name: 'Seychelles', flag: '🇸🇨' },
    { name: 'Madagascar', flag: '🇲🇬' },
    { name: 'Cabo Verde', flag: '🇨🇻' },
    { name: 'Zanzíbar', flag: '🇹🇿' },
    { name: 'Namibia', flag: '🇳🇦' },
    { name: 'Botsuana', flag: '🇧🇼' },
    { name: 'Ruanda', flag: '🇷🇼' },
    { name: 'Uganda', flag: '🇺🇬' },
    { name: 'Etiopía', flag: '🇪🇹' },
    { name: 'Ghana', flag: '🇬🇭' },
    { name: 'Nigeria', flag: '🇳🇬' },
    { name: 'Senegal', flag: '🇸🇳' },
    { name: 'Costa de Marfil', flag: '🇨🇮' },
    { name: 'Mozambique', flag: '🇲🇿' },
    { name: 'Zimbabue', flag: '🇿🇼' },
    { name: 'Zambia', flag: '🇿🇲' },
    { name: 'Malaui', flag: '🇲🇼' },
    { name: 'Gambia', flag: '🇬🇲' },
    { name: 'Reunión', flag: '🇷🇪' },
    // Oceanía
    { name: 'Australia', flag: '🇦🇺' },
    { name: 'Nueva Zelanda', flag: '🇳🇿' },
    { name: 'Fiyi', flag: '🇫🇯' },
    { name: 'Polinesia Francesa', flag: '🇵🇫' },
    { name: 'Hawái', flag: '🇺🇸' },
    { name: 'Samoa', flag: '🇼🇸' },
    { name: 'Tonga', flag: '🇹🇴' },
    { name: 'Vanuatu', flag: '🇻🇺' },
    { name: 'Nueva Caledonia', flag: '🇳🇨' },
    { name: 'Islas Cook', flag: '🇨🇰' },
    { name: 'Bora Bora', flag: '🇵🇫' },
    { name: 'Tahití', flag: '🇵🇫' },
    { name: 'Palau', flag: '🇵🇼' },
    { name: 'Guam', flag: '🇬🇺' },
    { name: 'Islas Salomón', flag: '🇸🇧' },
    { name: 'Papua Nueva Guinea', flag: '🇵🇬' },
    { name: 'Micronesia', flag: '🇫🇲' },
    { name: 'Islas Marshall', flag: '🇲🇭' },
  ];

  useEffect(() => {
    fetchPackages();
  }, [selectedCountry]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      let url = `${API}/certificates?status=active`;
      if (selectedCountry) {
        url += `&country=${encodeURIComponent(selectedCountry)}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter packages
  const filteredPackages = useMemo(() => {
    return packages.filter(pkg => {
      const matchesSearch = !searchTerm || 
        pkg.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.country?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPrice = priceRange === 'all' ||
        (priceRange === 'under500' && pkg.retail_price < 500) ||
        (priceRange === '500-1000' && pkg.retail_price >= 500 && pkg.retail_price <= 1000) ||
        (priceRange === 'over1000' && pkg.retail_price > 1000);
      
      const matchesDateType = dateType === 'all' || pkg.date_type === dateType;
      
      return matchesSearch && matchesPrice && matchesDateType;
    });
  }, [packages, searchTerm, priceRange, dateType]);

  // Get packages with coordinates for map
  const packagesWithCoords = useMemo(() => {
    return filteredPackages.filter(pkg => pkg.latitude && pkg.longitude);
  }, [filteredPackages]);

  // Get map center based on selected country
  const mapConfig = useMemo(() => {
    if (selectedCountry && COUNTRY_CENTERS[selectedCountry]) {
      return COUNTRY_CENTERS[selectedCountry];
    }
    return COUNTRY_CENTERS['default'];
  }, [selectedCountry]);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="search-resorts-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[1000] bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <Plane className="w-5 h-5 text-white transform -rotate-45" />
              </div>
              <span className="font-heading text-xl font-bold text-slate-900">Bombazoo</span>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/')} size="sm">
                <HomeIcon className="w-4 h-4 mr-1" />
                Inicio
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin')} size="sm">
                Admin
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="pt-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Explorar Resorts</h1>
          <p className="text-slate-300 mb-6 text-sm">
            {filteredPackages.length} destinos disponibles {selectedCountry && `en ${selectedCountry}`}
          </p>

          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar destino..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              />
            </div>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="px-4 py-3 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[180px] text-sm"
              data-testid="country-select"
            >
              <option value="">🌎 Todos los países</option>
              <optgroup label="🌎 América del Norte">
                <option value="México">🇲🇽 México</option>
                <option value="Estados Unidos">🇺🇸 Estados Unidos</option>
                <option value="Canadá">🇨🇦 Canadá</option>
              </optgroup>
              <optgroup label="🏝️ Caribe">
                <option value="República Dominicana">🇩🇴 República Dominicana</option>
                <option value="Cuba">🇨🇺 Cuba</option>
                <option value="Puerto Rico">🇵🇷 Puerto Rico</option>
                <option value="Jamaica">🇯🇲 Jamaica</option>
                <option value="Bahamas">🇧🇸 Bahamas</option>
                <option value="Aruba">🇦🇼 Aruba</option>
                <option value="Curazao">🇨🇼 Curazao</option>
                <option value="Barbados">🇧🇧 Barbados</option>
                <option value="Trinidad y Tobago">🇹🇹 Trinidad y Tobago</option>
                <option value="San Martín">🇸🇽 San Martín</option>
                <option value="Islas Caimán">🇰🇾 Islas Caimán</option>
                <option value="Islas Vírgenes">🇻🇬 Islas Vírgenes</option>
                <option value="Turks y Caicos">🇹🇨 Turks y Caicos</option>
                <option value="Antigua y Barbuda">🇦🇬 Antigua y Barbuda</option>
                <option value="Santa Lucía">🇱🇨 Santa Lucía</option>
                <option value="Granada">🇬🇩 Granada</option>
                <option value="San Vicente">🇻🇨 San Vicente</option>
              </optgroup>
              <optgroup label="🌴 América Central">
                <option value="Costa Rica">🇨🇷 Costa Rica</option>
                <option value="Panamá">🇵🇦 Panamá</option>
                <option value="Guatemala">🇬🇹 Guatemala</option>
                <option value="Belice">🇧🇿 Belice</option>
                <option value="Honduras">🇭🇳 Honduras</option>
                <option value="Nicaragua">🇳🇮 Nicaragua</option>
                <option value="El Salvador">🇸🇻 El Salvador</option>
              </optgroup>
              <optgroup label="🌎 América del Sur">
                <option value="Colombia">🇨🇴 Colombia</option>
                <option value="Perú">🇵🇪 Perú</option>
                <option value="Brasil">🇧🇷 Brasil</option>
                <option value="Argentina">🇦🇷 Argentina</option>
                <option value="Chile">🇨🇱 Chile</option>
                <option value="Ecuador">🇪🇨 Ecuador</option>
                <option value="Uruguay">🇺🇾 Uruguay</option>
                <option value="Venezuela">🇻🇪 Venezuela</option>
                <option value="Bolivia">🇧🇴 Bolivia</option>
                <option value="Paraguay">🇵🇾 Paraguay</option>
              </optgroup>
              <optgroup label="🏰 Europa">
                <option value="España">🇪🇸 España</option>
                <option value="Portugal">🇵🇹 Portugal</option>
                <option value="Francia">🇫🇷 Francia</option>
                <option value="Italia">🇮🇹 Italia</option>
                <option value="Reino Unido">🇬🇧 Reino Unido</option>
                <option value="Alemania">🇩🇪 Alemania</option>
                <option value="Grecia">🇬🇷 Grecia</option>
                <option value="Croacia">🇭🇷 Croacia</option>
                <option value="Suiza">🇨🇭 Suiza</option>
                <option value="Países Bajos">🇳🇱 Países Bajos</option>
                <option value="Turquía">🇹🇷 Turquía</option>
                <option value="Noruega">🇳🇴 Noruega</option>
                <option value="Islandia">🇮🇸 Islandia</option>
              </optgroup>
              <optgroup label="🌏 Asia">
                <option value="Tailandia">🇹🇭 Tailandia</option>
                <option value="Indonesia">🇮🇩 Indonesia</option>
                <option value="Vietnam">🇻🇳 Vietnam</option>
                <option value="Filipinas">🇵🇭 Filipinas</option>
                <option value="Malasia">🇲🇾 Malasia</option>
                <option value="Singapur">🇸🇬 Singapur</option>
                <option value="Japón">🇯🇵 Japón</option>
                <option value="Corea del Sur">🇰🇷 Corea del Sur</option>
                <option value="China">🇨🇳 China</option>
                <option value="India">🇮🇳 India</option>
                <option value="Maldivas">🇲🇻 Maldivas</option>
                <option value="Sri Lanka">🇱🇰 Sri Lanka</option>
              </optgroup>
              <optgroup label="🕌 Medio Oriente">
                <option value="Emiratos Árabes Unidos">🇦🇪 Emiratos Árabes Unidos</option>
                <option value="Qatar">🇶🇦 Qatar</option>
                <option value="Jordania">🇯🇴 Jordania</option>
                <option value="Israel">🇮🇱 Israel</option>
                <option value="Omán">🇴🇲 Omán</option>
              </optgroup>
              <optgroup label="🌍 África">
                <option value="Marruecos">🇲🇦 Marruecos</option>
                <option value="Egipto">🇪🇬 Egipto</option>
                <option value="Sudáfrica">🇿🇦 Sudáfrica</option>
                <option value="Kenia">🇰🇪 Kenia</option>
                <option value="Tanzania">🇹🇿 Tanzania</option>
                <option value="Mauricio">🇲🇺 Mauricio</option>
                <option value="Seychelles">🇸🇨 Seychelles</option>
              </optgroup>
              <optgroup label="🏝️ Oceanía">
                <option value="Australia">🇦🇺 Australia</option>
                <option value="Nueva Zelanda">🇳🇿 Nueva Zelanda</option>
                <option value="Fiyi">🇫🇯 Fiyi</option>
                <option value="Polinesia Francesa">🇵🇫 Polinesia Francesa</option>
                <option value="Hawái">🇺🇸 Hawái</option>
                <option value="Bora Bora">🇵🇫 Bora Bora</option>
                <option value="Tahití">🇵🇫 Tahití</option>
              </optgroup>
            </select>
            <Button 
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? "default" : "outline"}
              className={`px-4 py-3 rounded-xl h-auto ${showFilters ? 'bg-teal-500 text-white' : 'bg-white text-slate-700'}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
            </Button>
          </div>

          {/* Popular Countries Bar */}
          <div className="mt-4 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex gap-2 min-w-max">
              <button
                onClick={() => setSelectedCountry('')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  !selectedCountry 
                    ? 'bg-teal-500 text-white shadow-lg' 
                    : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                }`}
              >
                🌎 Todos
              </button>
              {COUNTRY_BAR.map((country) => (
                <button
                  key={country.name}
                  onClick={() => setSelectedCountry(country.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCountry === country.name 
                      ? 'bg-teal-500 text-white shadow-lg' 
                      : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                  }`}
                >
                  {country.flag} {country.name}
                </button>
              ))}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-3 p-4 bg-white rounded-xl shadow-lg">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Precio</label>
                  <select
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="under500">{"< $500"}</option>
                    <option value="500-1000">$500 - $1,000</option>
                    <option value="over1000">{"> $1,000"}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Fechas</label>
                  <select
                    value={dateType}
                    onChange={(e) => setDateType(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="open">🟢 Abiertas</option>
                    <option value="restricted">🟡 Restringidas</option>
                    <option value="exact">🔴 Exactas</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPriceRange('all');
                      setDateType('all');
                      setSelectedCountry('');
                      setSearchTerm('');
                    }}
                    className="text-slate-500 text-xs"
                  >
                    Limpiar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Toggle & Map */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{filteredPackages.length}</span> resorts
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-teal-500' : ''}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('map')}
              className={viewMode === 'map' ? 'bg-teal-500' : ''}
            >
              <Map className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Map View */}
        {viewMode === 'map' && (
          <div className="mb-6 rounded-2xl overflow-hidden shadow-lg border border-slate-200 relative" style={{ height: '500px' }}>
            <MapContainer
              center={[mapConfig.lat, mapConfig.lng]}
              zoom={mapConfig.zoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapUpdater center={mapConfig} zoom={mapConfig.zoom} />
              {packagesWithCoords.map((pkg) => (
                <Marker
                  key={pkg.certificate_id}
                  position={[pkg.latitude, pkg.longitude]}
                  icon={createPriceIcon(pkg.retail_price)}
                >
                  <Popup>
                    <div className="p-2 min-w-[200px]">
                      {pkg.images?.[0] && (
                        <img src={pkg.images[0]} alt={pkg.title} className="w-full h-24 object-cover rounded-lg mb-2" />
                      )}
                      <h3 className="font-bold text-slate-900 text-sm">{pkg.title}</h3>
                      <p className="text-xs text-slate-500 mb-2">{pkg.destination}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-teal-600">${pkg.retail_price.toLocaleString()}</span>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/paquete/${pkg.certificate_id}`)}
                          className="bg-teal-500 hover:bg-teal-600 text-xs"
                        >
                          Ver
                        </Button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            {/* Overlay for no resorts - show on top of map */}
            {selectedCountry && packagesWithCoords.length === 0 && filteredPackages.length === 0 && (
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg z-[500]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">No hay resorts en {selectedCountry} aún</p>
                    <p className="text-slate-500 text-sm">Pronto agregaremos ofertas increíbles para este destino</p>
                  </div>
                </div>
              </div>
            )}
            {selectedCountry && packagesWithCoords.length === 0 && filteredPackages.length > 0 && (
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg z-[500]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Hay {filteredPackages.length} resorts en {selectedCountry}</p>
                    <p className="text-slate-500 text-sm">Los marcadores aparecerán cuando agreguemos las ubicaciones exactas</p>
                  </div>
                </div>
              </div>
            )}
            {!selectedCountry && (
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg z-[500]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <Map className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Explora destinos en el mapa</p>
                    <p className="text-slate-500 text-sm">Selecciona un país de la barra o dropdown para ver sus resorts</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Grid View */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl">
            <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No se encontraron resorts</h3>
            <p className="text-slate-500 mb-6">Intenta ajustar tus filtros</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredPackages.map((pkg) => (
              <PackageCard
                key={pkg.certificate_id}
                pkg={pkg}
                onViewDetails={() => navigate(`/paquete/${pkg.certificate_id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <Plane className="w-4 h-4 text-white transform -rotate-45" />
            </div>
            <span className="font-bold">Bombazoo</span>
          </div>
          <p className="text-slate-400 text-sm">© 2024 Bombazoo. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
